import React, { useMemo } from 'react';
import { WeatherCondition } from '../../api/weather/types';
import { usePerformanceMonitor } from '../../hooks/weather/usePerformanceMonitor';
import UIcon from '../../assets/uicons/UIcon';
import styles from './WeatherInfo.module.scss';

interface WeatherInfoProps {
  temperature?: number;
  condition?: WeatherCondition;
  description?: string;
  location?: string;
  isVisible: boolean;
  isLoading?: boolean;
  isFallback?: boolean;
  fallbackReason?: string;
  hasError?: boolean;
  errorSeverity?: 'low' | 'medium' | 'high';
}

const WeatherInfo: React.FC<WeatherInfoProps> = React.memo(({
  temperature,
  condition = 'default',
  description,
  location,
  isVisible,
  isLoading = false,
  isFallback = false,
  fallbackReason,
  hasError = false,
  errorSeverity = 'medium'
}) => {
  // Performance monitoring
  usePerformanceMonitor(
    { componentName: 'WeatherInfo', trackProps: true },
    [temperature, condition, description, location, isVisible, isLoading, isFallback, hasError]
  );

  const weatherIcon = useMemo(() => {
    const iconMap: Record<WeatherCondition, string> = {
      'clear-day': 'sun',
      'clear-night': 'moon',
      'cloudy': 'cloud',
      'partly-cloudy': 'cloud-sun',
      'rain': 'cloud-rain',
      'snow': 'cloud-snow',
      'fog': 'wind',
      'default': 'sunrise'
    };
    return iconMap[condition];
  }, [condition]);

  const formattedTemperature = useMemo(() => {
    if (temperature === undefined) return '--°C';
    const temp = `${Math.round(temperature)}°C`;
    return isFallback ? `~${temp}` : temp; // Add ~ for estimated temperatures
  }, [temperature, isFallback]);

  const formattedLocation = useMemo(() => {
    if (!location) return '';
    // Limit location length for responsive design
    return location.length > 20 ? `${location.substring(0, 17)}...` : location;
  }, [location]);

  const statusIndicator = useMemo(() => {
    if (hasError) {
      return {
        icon: errorSeverity === 'high' ? 'x-circle' : errorSeverity === 'medium' ? 'exclamation-triangle' : 'circle',
        text: 'Error',
        className: styles[`status--error-${errorSeverity}`]
      };
    }
    
    if (isFallback) {
      return {
        icon: 'zap',
        text: 'Estimado',
        className: styles['status--fallback']
      };
    }
    
    return null;
  }, [hasError, isFallback, errorSeverity]);

  if (!isVisible && !isLoading) {
    return null;
  }

  return (
    <div className={`${styles.weatherInfo} ${isVisible ? styles.visible : styles.hidden} ${isFallback ? styles.fallback : ''} ${hasError ? styles.error : ''}`}>
      {isLoading ? (
        <div className={styles.loading}>
          <div className={styles.loadingSkeleton}>
            <div className={styles.iconSkeleton}></div>
            <div className={styles.textSkeleton}></div>
          </div>
        </div>
      ) : (
        <div className={styles.content}>
          <div className={styles.iconContainer}>
            <div
              role="img"
              aria-label={`Weather condition: ${description || condition}${isFallback ? ' (estimated)' : ''}`}
              className={`${styles.weatherIcon} ${isFallback ? styles.fallbackIcon : ''}`}
            >
              <UIcon 
                name={weatherIcon}
                size={24}
              />
            </div>
            
            {/* Status indicator for fallback/error states */}
            {statusIndicator && (
              <div className={`${styles.statusIndicator} ${statusIndicator.className}`}>
                <UIcon 
                  name={statusIndicator.icon}
                  size={12}
                  className={styles.statusIcon}
                />
              </div>
            )}
          </div>
          
          <div className={styles.weatherDetails}>
            <div className={`${styles.temperature} ${isFallback ? styles.fallbackText : ''}`}>
              {formattedTemperature}
            </div>
            
            {description && (
              <div className={`${styles.description} ${isFallback ? styles.fallbackText : ''}`}>
                {description}
              </div>
            )}
            
            {location && (
              <div className={`${styles.location} ${isFallback ? styles.fallbackText : ''}`}>
                {formattedLocation}
              </div>
            )}
            
            {/* Fallback reason tooltip */}
            {isFallback && fallbackReason && (
              <div className={styles.fallbackReason} title={`Datos estimados: ${fallbackReason}`}>
                <small>Datos estimados</small>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default WeatherInfo;